import React, { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiClient, uploadFile, API_BASE } from '../api/client';
import { usePreferences } from '../contexts/PreferencesContext';
import { usePageAuthorization } from '../hooks/usePageAuthorization';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { useIsMobile } from '../hooks/useIsMobile';

interface Venue {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
}

interface EventItem {
  id: string;
  slug?: string | null;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  maxCapacity: number;
  posterUrl?: string | null;
  specialMessage?: string | null;
  programUrl?: string | null;
  mealInfo?: string | null;
  dressCode?: string | null;
  transportInfo?: string | null;
  parkingInfo?: string | null;
  presenterInstructions?: string | null;
  affiliationLabel?: string | null;
  venue?: Venue;
  venueId: string;
  _count?: { registrations: number; vendorRegistrations?: number };
}

interface EventVendor {
  registrationId: string;
  id: string;
  businessName: string;
  contactPerson: string;
  email: string;
  phone: string | null;
  category: string | null;
  status: string;
  registrationDate: string;
}

interface EventFormData {
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  venueId: string;
  maxCapacity: string;
  mealInfo: string;
  dressCode: string;
  transportInfo: string;
  parkingInfo: string;
  presenterInstructions: string;
  affiliationLabel: string;
}

interface RsvpState {
  status: 'attending' | 'not_attending' | null;
  count: number;
}

const emptyForm: EventFormData = {
  name: '',
  date: '',
  startTime: '',
  endTime: '',
  venueId: '',
  maxCapacity: '',
  mealInfo: '',
  dressCode: '',
  transportInfo: '',
  parkingInfo: '',
  presenterInstructions: '',
  affiliationLabel: 'Affiliation',
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '2rem',
    maxWidth: '1000px',
    margin: '0 auto',
  },
  pageMobile: {
    padding: '1rem',
    paddingBottom: 'calc(72px + env(safe-area-inset-bottom))',
    maxWidth: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  headerMobile: {
    flexDirection: 'column' as const,
    alignItems: 'stretch',
    gap: '0.75rem',
  },
  eventCardMobile: {
    flexDirection: 'column' as const,
    alignItems: 'stretch',
    gap: '0.5rem',
  },
  title: {
    color: '#1a6b3c',
    fontSize: '1.75rem',
    margin: 0,
  },
  addButton: {
    padding: '0.6rem 1.2rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  dayGroup: {
    marginBottom: '2rem',
  },
  dayTitle: {
    color: '#0f2d1f',
    fontSize: '1.1rem',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.25rem',
    marginBottom: '0.75rem',
  },
  eventCard: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '1rem 1.25rem',
    marginBottom: '0.75rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  eventInfo: {
    flex: 1,
  },
  eventName: {
    fontWeight: 600,
    fontSize: '1rem',
    color: '#0f2d1f',
    marginBottom: '0.25rem',
  },
  eventMeta: {
    fontSize: '0.85rem',
    color: '#555',
  },
  capacityBar: {
    width: '100px',
    height: '8px',
    backgroundColor: '#e0e0e0',
    borderRadius: '4px',
    overflow: 'hidden',
    display: 'inline-block',
    marginLeft: '0.5rem',
    verticalAlign: 'middle',
  },
  capacityFill: {
    height: '100%',
    backgroundColor: '#1a6b3c',
    borderRadius: '4px',
    transition: 'width 0.3s',
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
  },
  editBtn: {
    padding: '0.25rem 0.4rem',
    backgroundColor: 'transparent',
    color: '#666',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    lineHeight: 1,
  },
  deleteBtn: {
    padding: '0.25rem 0.4rem',
    backgroundColor: 'transparent',
    color: '#999',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    lineHeight: 1,
  },
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '2rem',
    width: '100%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
  },
  modalTitle: {
    color: '#1a6b3c',
    marginBottom: '1.25rem',
    fontSize: '1.25rem',
  },
  field: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box' as const,
    backgroundColor: '#fff',
  },
  submitBtn: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  cancelBtn: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#eee',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
};

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  // Parse the date portion directly (stored as Eastern, no conversion needed)
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day); // Local date, no UTC shift
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  // Extract HH:MM from stored value (stored as Eastern, no conversion needed)
  let hours: number;
  let minutes: number;

  if (timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (timeStr.includes(':')) {
    [hours, minutes] = timeStr.split(':').map(Number);
  } else {
    return timeStr;
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function groupByDate(events: EventItem[]): Record<string, EventItem[]> {
  const groups: Record<string, EventItem[]> = {};
  const sorted = [...events].sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    return a.startTime.localeCompare(b.startTime);
  });
  for (const evt of sorted) {
    // Use just the date portion (YYYY-MM-DD) as the grouping key
    const key = evt.date.split('T')[0];
    if (!groups[key]) groups[key] = [];
    groups[key].push(evt);
  }
  return groups;
}

function getDirectionsUrl(venue?: Venue): string | null {
  if (!venue) return null;
  const parts = [venue.address, venue.city, venue.state].filter(Boolean);
  if (parts.length === 0) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
}

export function EventsPage() {
  const { preferences } = usePreferences();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { canEdit: pageCanEdit, isLoading: authLoading } = usePageAuthorization('events');
  // Editing an org's own events is core functionality for its operational
  // roles, not a configurable permission. A Customer Editor/Super User (or any
  // higher role) can always edit, regardless of Page Access Control rules.
  // Page Access Control can still GRANT edit to lower/custom roles on top of this.
  const canEdit = pageCanEdit || isAtLeast(user?.role ?? '', 'CustomerEditor');
  const [events, setEvents] = useState<EventItem[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EventFormData>(emptyForm);
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(() =>
    preferences.defaultViewMode === 'table' ? 'table' : 'cards'
  );

  // Vendor state for event vendors view
  const [expandedVendorsEventId, setExpandedVendorsEventId] = useState<string | null>(null);
  const [eventVendors, setEventVendors] = useState<EventVendor[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);

  // RSVP state
  const [rsvpMap, setRsvpMap] = useState<Record<string, RsvpState>>({});

  async function fetchEvents() {
    try {
      const data = await apiClient<EventItem[]>('/events');
      setEvents(data);
    } catch {
      setEvents([]);
    }
  }

  async function fetchRsvpData(eventList: EventItem[]) {
    const map: Record<string, RsvpState> = {};
    await Promise.all(
      eventList.map(async (evt) => {
        try {
          const [rsvpRes, countRes] = await Promise.all([
            apiClient<{ status: string | null }>(`/events/${evt.id}/event-rsvp`),
            apiClient<{ attending: number; notAttending: number }>(`/events/${evt.id}/event-rsvp-count`),
          ]);
          map[evt.id] = {
            status: rsvpRes.status as RsvpState['status'],
            count: countRes.attending,
          };
        } catch {
          map[evt.id] = { status: null, count: 0 };
        }
      }),
    );
    setRsvpMap(map);
  }

  async function handleRsvp(eventId: string, status: 'attending' | 'not_attending') {
    try {
      await apiClient(`/events/${eventId}/event-rsvp`, {
        method: 'POST',
        body: { status },
      });
      // Optimistic update
      setRsvpMap((prev) => {
        const current = prev[eventId] || { status: null, count: 0 };
        const wasAttending = current.status === 'attending';
        const nowAttending = status === 'attending';
        let newCount = current.count;
        if (wasAttending && !nowAttending) newCount = Math.max(0, newCount - 1);
        if (!wasAttending && nowAttending) newCount = newCount + 1;
        return { ...prev, [eventId]: { status, count: newCount } };
      });
    } catch {
      // Silently fail — user can retry
    }
  }

  async function toggleEventVendors(eventId: string) {
    if (expandedVendorsEventId === eventId) {
      setExpandedVendorsEventId(null);
      setEventVendors([]);
      return;
    }
    setExpandedVendorsEventId(eventId);
    setVendorsLoading(true);
    try {
      const data = await apiClient<EventVendor[]>(`/events/${eventId}/vendors`);
      setEventVendors(data);
    } catch {
      setEventVendors([]);
    } finally {
      setVendorsLoading(false);
    }
  }

  async function fetchVenues() {
    try {
      const data = await apiClient<Venue[]>('/venues');
      setVenues(data);
    } catch {
      setVenues([]);
    }
  }

  useEffect(() => {
    fetchEvents();
    fetchVenues();
  }, []);

  useEffect(() => {
    if (events.length > 0) {
      fetchRsvpData(events);
    }
  }, [events.length]);

  function openCreate() {
    setForm(emptyForm);
    setEditingId(null);
    setFormError('');
    setShowModal(true);
  }

  function openEdit(evt: EventItem) {
    // Extract date (YYYY-MM-DD) and time (HH:MM) directly from ISO strings
    const dateStr = evt.date ? evt.date.split('T')[0] : '';
    const startStr = evt.startTime && evt.startTime.includes('T')
      ? evt.startTime.split('T')[1].slice(0, 5)
      : evt.startTime || '';
    const endStr = evt.endTime && evt.endTime.includes('T')
      ? evt.endTime.split('T')[1].slice(0, 5)
      : evt.endTime || '';

    setForm({
      name: evt.name,
      date: dateStr,
      startTime: startStr,
      endTime: endStr,
      venueId: evt.venueId || (evt.venue?.id ?? ''),
      maxCapacity: String(evt.maxCapacity),
      mealInfo: evt.mealInfo || '',
      dressCode: evt.dressCode || '',
      transportInfo: evt.transportInfo || '',
      parkingInfo: evt.parkingInfo || '',
      presenterInstructions: evt.presenterInstructions || '',
      affiliationLabel: evt.affiliationLabel || 'Affiliation',
    });
    setEditingId(evt.id);
    setFormError('');
    setShowModal(true);
  }

  async function handleDelete(evt: EventItem) {
    if (!confirm(`Are you sure you want to delete "${evt.name}"? This will remove all associated RSVPs.`)) {
      return;
    }
    try {
      await apiClient(`/events/${evt.id}`, { method: 'DELETE' });
      fetchEvents();
    } catch (err: unknown) {
      const apiError = err as { message?: string };
      alert(apiError.message || 'Failed to delete event.');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');

    if (!form.name || !form.date || !form.startTime || !form.endTime || !form.venueId || !form.maxCapacity) {
      setFormError('All fields are required.');
      return;
    }

    setLoading(true);

    // Store times as-entered (Eastern time) — no UTC conversion
    // Format: "2026-07-03T19:00:00" (no Z suffix = treated as local/Eastern)
    const startISO = `${form.date}T${form.startTime}:00`;
    const endISO = `${form.date}T${form.endTime}:00`;

    const payload: Record<string, unknown> = {
      name: form.name,
      date: form.date,
      startTime: startISO,
      endTime: endISO,
      venueId: form.venueId,
      maxCapacity: parseInt(form.maxCapacity, 10),
    };
    if (form.mealInfo) payload.mealInfo = form.mealInfo;
    if (form.dressCode) payload.dressCode = form.dressCode;
    if (form.transportInfo) payload.transportInfo = form.transportInfo;
    if (form.parkingInfo) payload.parkingInfo = form.parkingInfo;
    if (form.presenterInstructions) payload.presenterInstructions = form.presenterInstructions;
    if (form.affiliationLabel) payload.affiliationLabel = form.affiliationLabel.trim();

    try {
      if (editingId) {
        await apiClient(`/events/${editingId}`, { method: 'PATCH', body: payload });
      } else {
        await apiClient('/events', { method: 'POST', body: payload });
      }
      setShowModal(false);
      fetchEvents();
    } catch (err: unknown) {
      const apiError = err as { message?: string };
      setFormError(apiError.message || 'Failed to save event.');
    } finally {
      setLoading(false);
    }
  }

  const grouped = groupByDate(events);
  const sortedEvents = [...events].sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    return a.startTime.localeCompare(b.startTime);
  });

  if (authLoading) {
    return (
      <div style={styles.page}>
        <p style={{ color: '#666' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ ...styles.page, ...(isMobile ? styles.pageMobile : null) }}>
      <div style={{ ...styles.header, ...(isMobile ? styles.headerMobile : null) }}>
        <h1 style={styles.title}>Events</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <Link
            to="/events/snapshots"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.5rem 0.9rem', fontSize: '0.85rem', fontWeight: 500, color: '#1a6b3c', backgroundColor: '#eef6f0', border: '1px solid #d0e8d6', borderRadius: '4px', textDecoration: 'none', cursor: 'pointer' }}
          >
            📸 Event Snapshots
          </Link>
          <button
            style={viewMode === 'cards' ? { ...styles.addButton, backgroundColor: '#1a6b3c' } : { ...styles.addButton, backgroundColor: '#e0e0e0', color: '#333' }}
            onClick={() => setViewMode('cards')}
          >Cards</button>
          <button
            style={viewMode === 'table' ? { ...styles.addButton, backgroundColor: '#1a6b3c' } : { ...styles.addButton, backgroundColor: '#e0e0e0', color: '#333' }}
            onClick={() => setViewMode('table')}
          >Table</button>
          {canEdit && <button style={styles.addButton} onClick={openCreate}>+ Add Event</button>}
        </div>
      </div>

      {events.length === 0 && (
        <p style={{ color: '#666' }}>
          {canEdit ? 'No events yet. Click "Add Event" to create one.' : 'No events yet.'}
        </p>
      )}

      {viewMode === 'cards' ? (
        /* Card View */
        Object.entries(grouped).map(([date, dayEvents]) => (
          <div key={date} style={styles.dayGroup}>
            <h3 style={styles.dayTitle}>{formatDate(date)}</h3>
            {dayEvents.map((evt) => {
              const registered = evt._count?.registrations || 0;
              const capacityPct = Math.min((registered / evt.maxCapacity) * 100, 100);
              return (
                <React.Fragment key={evt.id}>
                <div style={{ ...styles.eventCard, ...(isMobile ? styles.eventCardMobile : null) }}>
                  <div style={styles.eventInfo}>
                    <div style={styles.eventName}>{evt.name}</div>
                    <div style={styles.eventMeta}>
                      {formatTime(evt.startTime)} – {formatTime(evt.endTime)}
                      {evt.venue && <> · {evt.venue.name}</>}
                      {evt.venue && getDirectionsUrl(evt.venue) && (
                        <a
                          href={getDirectionsUrl(evt.venue)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginLeft: '0.4rem', fontSize: '0.8rem', color: '#1a73e8', textDecoration: 'none' }}
                        >
                          📍 Directions
                        </a>
                      )}
                      <span style={{ marginLeft: '1rem' }}>
                        {registered}/{evt.maxCapacity}
                        <span style={styles.capacityBar}>
                          <span style={{ ...styles.capacityFill, width: `${capacityPct}%` }} />
                        </span>
                      </span>
                      <Link
                        to={`/events/${evt.slug || evt.id}/agenda`}
                        style={{ marginLeft: '1rem', fontSize: '0.85rem', color: '#1a6b3c', textDecoration: 'none', fontWeight: 500 }}
                      >
                        📋 Agenda
                      </Link>
                      <button
                        onClick={() => toggleEventVendors(evt.id)}
                        style={{ marginLeft: '1rem', background: 'none', border: 'none', color: '#1a6b3c', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, textDecoration: 'underline' }}
                      >
                        {expandedVendorsEventId === evt.id ? 'Hide Vendors' : 'Vendors'}
                      </button>
                    </div>
                    {/* Parking & Dress Code info pills */}
                    {(evt.parkingInfo || evt.dressCode) && (
                      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.4rem', flexWrap: 'wrap' as const }}>
                        {evt.parkingInfo && (
                          <span style={{ fontSize: '0.8rem', color: '#4a5568', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            🅿️ {evt.parkingInfo}
                          </span>
                        )}
                        {evt.dressCode && (
                          <span style={{ fontSize: '0.8rem', color: '#4a5568', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            👔 {evt.dressCode}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Special Message Banner */}
                    {evt.specialMessage && (
                      <div style={{
                        backgroundColor: '#fffbeb',
                        border: '1px solid #f59e0b',
                        borderRadius: '8px',
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        fontSize: '0.88rem',
                        color: '#92400e',
                        lineHeight: '1.4',
                        marginTop: '0.5rem',
                      }}>
                        <span aria-hidden="true">📢</span>
                        <span dangerouslySetInnerHTML={{ __html: evt.specialMessage }} />
                      </div>
                    )}
                    {/* View Program Button */}
                    {evt.programUrl && (
                      <a
                        href={evt.programUrl.startsWith('/') ? `${API_BASE}${evt.programUrl}` : evt.programUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          marginTop: '0.5rem',
                          padding: '6px 12px',
                          backgroundColor: '#f0f7f2',
                          border: '1px solid #c8e6c9',
                          borderRadius: '5px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          color: '#1a6b3c',
                          textDecoration: 'none',
                        }}
                      >
                        📄 View Program
                      </a>
                    )}
                    {/* RSVP Buttons */}
                    <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleRsvp(evt.id, 'attending')}
                        style={{
                          padding: '0.25rem 0.6rem',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          border: '1px solid',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          backgroundColor: rsvpMap[evt.id]?.status === 'attending' ? '#16a34a' : '#fff',
                          color: rsvpMap[evt.id]?.status === 'attending' ? '#fff' : '#16a34a',
                          borderColor: '#16a34a',
                        }}
                      >
                        ✓ I'm attending
                      </button>
                      <button
                        onClick={() => handleRsvp(evt.id, 'not_attending')}
                        style={{
                          padding: '0.25rem 0.6rem',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          border: '1px solid',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          backgroundColor: rsvpMap[evt.id]?.status === 'not_attending' ? '#6b7280' : '#fff',
                          color: rsvpMap[evt.id]?.status === 'not_attending' ? '#fff' : '#6b7280',
                          borderColor: '#6b7280',
                        }}
                      >
                        ✗ Can't make it
                      </button>
                      {(rsvpMap[evt.id]?.count ?? 0) > 0 && (
                        <span style={{ fontSize: '0.75rem', color: '#555', fontWeight: 500 }}>
                          {rsvpMap[evt.id].count} attending
                        </span>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                  <div style={{ ...styles.actions, ...(isMobile ? { justifyContent: 'flex-end', borderTop: '1px solid #f0f0f0', paddingTop: '0.5rem' } : null) }}>
                    <button style={styles.editBtn} onClick={() => openEdit(evt)} title="Edit">✏️</button>
                    <button style={styles.deleteBtn} onClick={() => handleDelete(evt)} title="Delete">🗑️</button>
                  </div>
                  )}
                </div>
                {expandedVendorsEventId === evt.id && (
                  <div style={{ marginBottom: '1rem', marginLeft: '1rem', padding: '0.75rem', backgroundColor: '#f9f9f9', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#0f2d1f', fontSize: '0.9rem' }}>Vendors for {evt.name}</h4>
                    {vendorsLoading ? (
                      <p style={{ color: '#666', fontSize: '0.85rem' }}>Loading...</p>
                    ) : eventVendors.length === 0 ? (
                      <p style={{ color: '#666', fontSize: '0.85rem' }}>No vendors registered for this event.</p>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Business</th>
                            <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Contact</th>
                            <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Category</th>
                            <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {eventVendors.map((v) => (
                            <tr key={v.registrationId}>
                              <td style={{ padding: '0.4rem', borderBottom: '1px solid #f0f0f0', fontWeight: 500 }}>{v.businessName}</td>
                              <td style={{ padding: '0.4rem', borderBottom: '1px solid #f0f0f0' }}>{v.contactPerson}</td>
                              <td style={{ padding: '0.4rem', borderBottom: '1px solid #f0f0f0' }}>{v.category || '—'}</td>
                              <td style={{ padding: '0.4rem', borderBottom: '1px solid #f0f0f0' }}>
                                <span style={{ padding: '0.15rem 0.4rem', borderRadius: '3px', fontSize: '0.75rem', fontWeight: 500, backgroundColor: v.status === 'Confirmed' ? '#e8f5e9' : v.status === 'Cancelled' ? '#fdecea' : '#fff3e0', color: v.status === 'Confirmed' ? '#2e7d32' : v.status === 'Cancelled' ? '#d32f2f' : '#e65100' }}>
                                  {v.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
                </React.Fragment>
              );
            })}
          </div>
        ))
      ) : (
        /* Table View */
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' }}>Event</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' }}>Date</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' }}>Time</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' }}>Venue</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' }}>Capacity</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' }}>Vendors</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' }}>RSVP</th>
                {canEdit && <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sortedEvents.map((evt) => {
                const registered = evt._count?.registrations || 0;
                const colCount = canEdit ? 8 : 7;
                return (
                  <React.Fragment key={evt.id}>
                  <tr style={{ borderBottom: expandedVendorsEventId === evt.id ? 'none' : '1px solid #e0e0e0' }}>
                    <td style={{ padding: '0.75rem 0.5rem', fontWeight: 600, color: '#0f2d1f' }}>
                      {evt.name}
                      <Link to={`/events/${evt.slug || evt.id}/agenda`} style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#1a6b3c', textDecoration: 'none' }}>📋</Link>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#333' }}>{formatDate(evt.date)}</td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#333' }}>{formatTime(evt.startTime)} – {formatTime(evt.endTime)}</td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#333' }}>
                      {evt.venue?.name || '—'}
                      {evt.venue && getDirectionsUrl(evt.venue) && (
                        <a
                          href={getDirectionsUrl(evt.venue)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginLeft: '0.4rem', fontSize: '0.75rem', color: '#1a73e8', textDecoration: 'none' }}
                        >
                          📍
                        </a>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#333' }}>{registered}/{evt.maxCapacity}</td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#333' }}>
                      <button
                        onClick={() => toggleEventVendors(evt.id)}
                        style={{ background: 'none', border: 'none', color: '#1a6b3c', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, textDecoration: 'underline' }}
                      >
                        {evt._count?.vendorRegistrations || 0}
                      </button>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleRsvp(evt.id, 'attending')}
                          style={{
                            padding: '0.2rem 0.4rem',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            border: '1px solid',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            backgroundColor: rsvpMap[evt.id]?.status === 'attending' ? '#16a34a' : '#fff',
                            color: rsvpMap[evt.id]?.status === 'attending' ? '#fff' : '#16a34a',
                            borderColor: '#16a34a',
                          }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => handleRsvp(evt.id, 'not_attending')}
                          style={{
                            padding: '0.2rem 0.4rem',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            border: '1px solid',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            backgroundColor: rsvpMap[evt.id]?.status === 'not_attending' ? '#6b7280' : '#fff',
                            color: rsvpMap[evt.id]?.status === 'not_attending' ? '#fff' : '#6b7280',
                            borderColor: '#6b7280',
                          }}
                        >
                          ✗
                        </button>
                        {(rsvpMap[evt.id]?.count ?? 0) > 0 && (
                          <span style={{ fontSize: '0.7rem', color: '#555' }}>{rsvpMap[evt.id].count}</span>
                        )}
                      </div>
                    </td>
                    {canEdit && (
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <button style={styles.editBtn} onClick={() => openEdit(evt)} title="Edit">✏️</button>
                      <button style={styles.deleteBtn} onClick={() => handleDelete(evt)} title="Delete">🗑️</button>
                    </td>
                    )}
                  </tr>
                  {expandedVendorsEventId === evt.id && (
                    <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td colSpan={colCount} style={{ padding: '0.75rem 1rem', backgroundColor: '#f9f9f9' }}>
                        <strong style={{ fontSize: '0.85rem', color: '#0f2d1f' }}>Vendors for {evt.name}</strong>
                        {vendorsLoading ? (
                          <p style={{ color: '#666', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>Loading...</p>
                        ) : eventVendors.length === 0 ? (
                          <p style={{ color: '#666', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>No vendors registered for this event.</p>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Business</th>
                                <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Contact</th>
                                <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Category</th>
                                <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {eventVendors.map((v) => (
                                <tr key={v.registrationId}>
                                  <td style={{ padding: '0.4rem', borderBottom: '1px solid #f0f0f0', fontWeight: 500 }}>{v.businessName}</td>
                                  <td style={{ padding: '0.4rem', borderBottom: '1px solid #f0f0f0' }}>{v.contactPerson}</td>
                                  <td style={{ padding: '0.4rem', borderBottom: '1px solid #f0f0f0' }}>{v.category || '—'}</td>
                                  <td style={{ padding: '0.4rem', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ padding: '0.15rem 0.4rem', borderRadius: '3px', fontSize: '0.75rem', fontWeight: 500, backgroundColor: v.status === 'Confirmed' ? '#e8f5e9' : v.status === 'Cancelled' ? '#fdecea' : '#fff3e0', color: v.status === 'Confirmed' ? '#2e7d32' : v.status === 'Cancelled' ? '#d32f2f' : '#e65100' }}>
                                      {v.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {canEdit && showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>{editingId ? 'Edit Event' : 'Add Event'}</h2>

            {formError && <div style={styles.error}>{formError}</div>}

            <form onSubmit={handleSubmit}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="evtName">Name</label>
                <input
                  id="evtName"
                  style={styles.input}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="evtDate">Date</label>
                <input
                  id="evtDate"
                  type="date"
                  style={styles.input}
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label} htmlFor="evtStart">Start Time</label>
                  <input
                    id="evtStart"
                    type="time"
                    style={styles.input}
                    value={form.startTime}
                    onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                  />
                </div>
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label} htmlFor="evtEnd">End Time</label>
                  <input
                    id="evtEnd"
                    type="time"
                    style={styles.input}
                    value={form.endTime}
                    onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                  />
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="evtVenue">Venue</label>
                <select
                  id="evtVenue"
                  style={styles.select}
                  value={form.venueId}
                  onChange={(e) => setForm({ ...form, venueId: e.target.value })}
                >
                  <option value="">Select a venue...</option>
                  {venues.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="evtCapacity">Max Capacity</label>
                <input
                  id="evtCapacity"
                  type="number"
                  min="1"
                  max="9999"
                  style={styles.input}
                  value={form.maxCapacity}
                  onChange={(e) => setForm({ ...form, maxCapacity: e.target.value })}
                />
              </div>

              {/* Event Details — 2-column grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="evtDressCode">Dress Code</label>
                  <input
                    id="evtDressCode"
                    style={styles.input}
                    placeholder="e.g. Black Tie, Smart Casual"
                    value={form.dressCode}
                    onChange={(e) => setForm({ ...form, dressCode: e.target.value })}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="evtMealInfo">Meal Info</label>
                  <input
                    id="evtMealInfo"
                    style={styles.input}
                    placeholder="e.g. Buffet dinner provided"
                    value={form.mealInfo}
                    onChange={(e) => setForm({ ...form, mealInfo: e.target.value })}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="evtTransport">Transport Info</label>
                  <input
                    id="evtTransport"
                    style={styles.input}
                    placeholder="e.g. Shuttle from hotel at 6pm"
                    value={form.transportInfo}
                    onChange={(e) => setForm({ ...form, transportInfo: e.target.value })}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="evtParking">Parking Info</label>
                  <input
                    id="evtParking"
                    style={styles.input}
                    placeholder="e.g. Free valet parking available"
                    value={form.parkingInfo}
                    onChange={(e) => setForm({ ...form, parkingInfo: e.target.value })}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="evtAffiliation">Affiliation label</label>
                  <input
                    id="evtAffiliation"
                    style={styles.input}
                    placeholder="e.g. Chapter, Company, Family, Department"
                    value={form.affiliationLabel}
                    onChange={(e) => setForm({ ...form, affiliationLabel: e.target.value })}
                  />
                  <p style={{ fontSize: '0.72rem', color: '#888', margin: '0.25rem 0 0' }}>
                    What this event calls a guest’s group (used in imports, seating, and guest cards).
                  </p>
                </div>
              </div>

              {/* Presenter Instructions — Rich Text Editor */}
              <div style={styles.field}>
                <label style={styles.label}>Presenter Instructions</label>
                <p style={{ fontSize: '0.75rem', color: '#666', margin: '0 0 0.4rem' }}>
                  Rich text notes for presenters (visible in Presenter Portal)
                </p>
                <div style={{ display: 'flex', gap: '2px', padding: '3px 5px', backgroundColor: '#f3f4f6', borderRadius: '4px 4px 0 0', border: '1px solid #ccc', borderBottom: 'none' }}>
                  <button type="button" onClick={() => document.execCommand('bold')} style={{ padding: '2px 6px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }} title="Bold">B</button>
                  <button type="button" onClick={() => document.execCommand('italic')} style={{ padding: '2px 6px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.8rem', fontStyle: 'italic' }} title="Italic">I</button>
                  <button type="button" onClick={() => document.execCommand('underline')} style={{ padding: '2px 6px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }} title="Underline">U</button>
                  <button type="button" onClick={() => document.execCommand('insertUnorderedList')} style={{ padding: '2px 6px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.8rem' }} title="Bullet List">•</button>
                  <button type="button" onClick={() => document.execCommand('insertOrderedList')} style={{ padding: '2px 6px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.8rem' }} title="Numbered List">1.</button>
                </div>
                <div
                  contentEditable
                  suppressContentEditableWarning
                  dangerouslySetInnerHTML={{ __html: form.presenterInstructions }}
                  onBlur={(e) => setForm({ ...form, presenterInstructions: (e.target as HTMLDivElement).innerHTML })}
                  style={{
                    minHeight: '100px',
                    padding: '0.6rem 0.75rem',
                    border: '1px solid #ccc',
                    borderRadius: '0 0 4px 4px',
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                    outline: 'none',
                    backgroundColor: '#fff',
                  }}
                />
              </div>

              {/* Poster Image Upload (only when editing) */}
              {editingId && (
                <div style={styles.field}>
                  <label style={styles.label}>Event Poster / Image</label>
                  {events.find(e => e.id === editingId)?.posterUrl && (
                    <div style={{ marginBottom: '8px' }}>
                      <img
                        src={events.find(e => e.id === editingId)!.posterUrl!}
                        alt="Current poster"
                        style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', borderRadius: '6px' }}
                      />
                      <button
                        type="button"
                        style={{ marginTop: '4px', background: 'none', border: 'none', color: '#d32f2f', fontSize: '0.8rem', cursor: 'pointer' }}
                        onClick={async () => {
                          try {
                            await apiClient(`/events/${editingId}/poster`, { method: 'DELETE' });
                            fetchEvents();
                          } catch {}
                        }}
                      >
                        🗑️ Remove Image
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    style={{ fontSize: '0.85rem' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        await uploadFile(`/events/${editingId}/poster`, file);
                        fetchEvents();
                      } catch {
                        setFormError('Image upload failed.');
                      }
                    }}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>JPEG, PNG, or WebP — max 10MB</span>
                </div>
              )}

              <button type="submit" style={styles.submitBtn} disabled={loading}>
                {loading ? 'Saving...' : editingId ? 'Update Event' : 'Create Event'}
              </button>
              <button type="button" style={styles.cancelBtn} onClick={() => setShowModal(false)}>
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
